import { MutableRefObject, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useGeneralEditableTableAPIFactory } from "./GeneralEditableTableAPI";
import { Typography, Box, Button, IconButton, CircularProgress } from "@mui/material";
import DeleteConfirmationDialog from "@arelia-components/ui-atoms/DeleteConfirmationDialog";
import { ColumnDef, DataGrid, editCells } from "@arelia-components/ui-atoms/data-grid";
import { ArelTooltip } from "@arelia-components/ui-atoms/Tooltip";
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { QueryParamDict } from "./constants";
import SpinnerWrapper from "@arelia-components/ui-atoms/SpinnerWrapper";

// symbol to mark that row is changed
const rowChangedSymbol = Symbol('row changed');

export interface GeneralEditableTableContainerApi {
  addRow: () => void;
  save: () => void;
}

export interface EditableTableProps<T> {
  columnConfig: ColumnDef[];
  apiRoute: string;

  onMutationSuccess: () => void;
  getApiResponseMapper?: (data: T[]) => any[];
  postApiResponseMapper?: (rowData: any[]) => T[];
  onAddRow: () => void | undefined;
  apiRef?: MutableRefObject<GeneralEditableTableContainerApi | undefined>;
  responseDataField?: string | null;

  getQueryPath?: string;
  getQueryDict?: QueryParamDict;
  getUseTake?: boolean;
  updateQueryPath?: string;
  updateQueryMethod?: string;
  deleteQueryPath?: string;
  deleteLocally?: boolean;
  bulkSendAll?: boolean;
  seeAllButton?: boolean;
  totalRecordKey?: string;
  tableItemName?: string;
  addToEmpty?: boolean;
}

export default function GeneralEditableTableContainer<T>({
  columnConfig,
  apiRoute,
  onMutationSuccess = () => { },
  getApiResponseMapper = (data: T[]) => data,
  postApiResponseMapper = (rowData: any[]) => rowData,
  onAddRow = () => { },
  apiRef,
  responseDataField = "data",
  getQueryPath = "",
  getQueryDict = {},
  getUseTake = true,
  updateQueryPath = "",
  updateQueryMethod = "POST",
  deleteQueryPath = "",
  deleteLocally = false,
  bulkSendAll = false,
  seeAllButton = true,
  totalRecordKey = "totalRecordCount",
  tableItemName = "Events",
  addToEmpty = false
}: EditableTableProps<T>) {
  const [rowToDelete, setRowToDelete] = useState<any | null>(null);
  const [allDataReceived, setAllDataReceived] = useState(false);
  const [gridData, setGridData] = useState<any[]>([]);
  const [gridState, setGridState] = useState<any>({});
  const [tableLoadingStatus, setTableLoadingStatus] = useState(true);

  const responseDataGetter = (data: T | undefined, field: string | null) => {
    if (!data) return [];
    if (!field) return data;
    return (data as any)[field];
  }

  const {
    apiResponse,
    isLoading,
    updateDataMutation,
    deleteDataMutation,
    getAllData
  } = useGeneralEditableTableAPIFactory<T>(
    apiRoute,
    getQueryPath,
    getQueryDict,
    getUseTake,
    updateQueryPath,
    updateQueryMethod,
    deleteQueryPath,
    onMutationSuccess
  );

  useEffect(() => {
    let originalData = responseDataGetter(apiResponse, responseDataField);

    if (getApiResponseMapper)
      originalData = getApiResponseMapper?.(originalData);

    setGridData(originalData.map((x: T) => ({ ...x })));
    setTableLoadingStatus(false);
  }, [apiResponse]);

  useImperativeHandle(apiRef, () => ({
    addRow: () => {
      const newRow = { id: `new__${crypto.randomUUID()}` };
      setGridData([...gridData, newRow]);
      setGridState(editCells(gridState, newRow.id, columnDefs.map(x => x.field as string)));
    },
    save: () => {
      let editedData: any[] = bulkSendAll
        ? gridData
        : gridData.filter(x => x[rowChangedSymbol]);

      if (postApiResponseMapper)
        editedData = postApiResponseMapper(editedData);

      if (!deleteLocally && editedData.length === 0)
        return;

      for (let row of editedData.filter(x => x.id.startsWith("new__")))
        delete row.id;

      updateDataMutation.mutate(editedData, {
        onSuccess: () => setGridState({})
      });
    }
  }));

  const receiveAllData = () => {
    setTableLoadingStatus(true);
    getAllData();
    setAllDataReceived(true);
  };

  const handleDeleteRow = (confirmed: boolean) => {
    if (confirmed) {
      if (rowToDelete.id.startsWith("new__")) {
        setGridData(data => data.filter(x => x !== rowToDelete));
      } else {
        deleteDataMutation.mutate(rowToDelete.id);
      }
    }

    setRowToDelete(null);
  };

  const handleRowUpdate = ({ row }: any) => row[rowChangedSymbol] = true;

  const rowDeleteAction = (rowData: any) => {
    const clickHandler = deleteLocally
      ? () => {
        setGridData(data => data.filter(x => x.id !== rowData.row.id))
      }
      : () => setRowToDelete(rowData.row);
    return (<ArelTooltip title={rowData.row.isReadOnly ? "Only manual records can be edited" : ""}>
      <Box>
        <IconButton aria-label="delete" onClick={clickHandler} disabled={rowData.row.isReadOnly} color="error">
          <RemoveCircleOutlineOutlinedIcon />
        </IconButton>
      </Box>
    </ArelTooltip>);
  };

  const columnDefs = useMemo(() => [...columnConfig, {
    field: "actions",
    renderer: (rowDeleteAction)
  } as ColumnDef], [columnConfig]);

  const totalRecordCount: number = !!apiResponse && Object.keys(apiResponse as any).includes(totalRecordKey)
    ? (apiResponse as any)[totalRecordKey]
    : gridData.length;

  return (
    <>
      <Box sx={styles.containerBox}>
        <SpinnerWrapper loading={isLoading || tableLoadingStatus}>
          {gridData.length > 0 ? (
            <>
              <DataGrid
                sx={styles.dataGrid}
                columns={columnDefs}
                rows={gridData}
                getRowStyle={({ row }) => ({ opacity: row.isReadOnly ? 0.5 : 1 })}
                isRowEditable={({ row }) => !row.isReadOnly}
                onRowUpdate={handleRowUpdate}
                state={gridState}
                onStateChange={setGridState}
              />

              {seeAllButton && totalRecordCount > 5 && !allDataReceived && (
                <Box sx={styles.seeAllButton}>
                  <Button variant="transparent" onClick={receiveAllData}>
                    See All {tableItemName}
                  </Button>
                </Box>
              )}
            </>
          ) : (
            !isLoading && !tableLoadingStatus && <Typography variant="subtitle1" sx={styles.noData}>No data to display</Typography>
          )}
        </SpinnerWrapper>
      </Box>

      {rowToDelete && (
        <DeleteConfirmationDialog
          message={
            <>
              Are you sure you want to delete this item? <br />
              This action cannot be undone and will discard all changes.
            </>
          }
          onClose={handleDeleteRow}
        />
      )}

      {(updateDataMutation.isLoading || deleteDataMutation.isLoading) && (
        <Box sx={styles.loadingOverlay}>
          <CircularProgress />
        </Box>
      )}
    </>
  );
}

const styles = {
  containerBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    minHeight: '300px',
    height: '100%',
    width: '100%'
  },
  noData: {
    textTransform: 'uppercase',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)'
  },
  dataGrid: {
    height: '100%',
    width: '100%',
    marginTop: 2,
    '& .MuiDataGrid-main': {
      paddingBottom: 0
    },
    '& .MuiDataGrid-footerContainer': {
      padding: '0 4px'
    }
  },
  seeAllButton: {
    display: 'flex',
    justifyContent: 'center',
    margin: '45px 0 10px'
  },
  loadingOverlay: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)'
  }
};
